import { useEffect, useMemo, useState } from "react";
import {
  ShieldAlert, ShieldCheck, UserPlus, KeyRound, Ban, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_ROLE_LABEL } from "@/components/AdminShell";

const ADMIN_ROLES = [
  "platform_owner", "platform_admin", "sales_admin", "sales_manager",
  "support_admin", "master_data_admin", "translation_admin",
  "analytics_admin", "tech_ops_admin", "compliance_admin", "readonly_exec",
];

const StatusPill = ({ active }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
      active ? "bg-[#12B76A]/10 text-[#12B76A]" : "bg-[#e63946]/10 text-[#e63946]"
    }`}
  >
    {active ? "Active" : "Locked"}
  </span>
);

const TwoFAPill = ({ on }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
      on ? "bg-[#457b9d]/10 text-[#457b9d]" : "bg-[#F79009]/10 text-[#F79009]"
    }`}
  >
    {on ? <ShieldCheck size={10} /> : <ShieldAlert size={10} />}
    {on ? "2FA on" : "2FA off"}
  </span>
);

export default function AdminMembers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", admin_role: "support_admin", password: "", requires_2fa: true });
  const [saving, setSaving] = useState(false);

  const isOwner = me?.admin_role === "platform_owner";

  const load = () => {
    setLoading(true);
    api.get("/admin/members")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submitInvite = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      toast.error("Name, email and password are required");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/members", form);
      toast.success("Admin invited");
      setInviteOpen(false);
      setForm({ name: "", email: "", admin_role: "support_admin", password: "", requires_2fa: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const patch = async (uid, payload, message) => {
    try {
      await api.patch(`/admin/members/${uid}`, payload);
      toast.success(message || "Updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const grouped = useMemo(() => {
    const map = { platform_owner: [], operations: [], readonly: [] };
    items.forEach((m) => {
      if (m.admin_role === "platform_owner") map.platform_owner.push(m);
      else if (m.admin_role === "readonly_exec") map.readonly.push(m);
      else map.operations.push(m);
    });
    return map;
  }, [items]);

  return (
    <div className="space-y-5" data-testid="admin-members-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Governance</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557]">Admin members</h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            {items.length} internal SparesHub {items.length === 1 ? "admin" : "admins"} · Only Platform Owner can invite or change roles.
          </p>
        </div>
        {isOwner && (
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="admin-members-invite-btn"
                className="h-9 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold text-[13px] px-3"
              >
                <UserPlus size={14} className="mr-1.5" /> Invite admin
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-lg border-[#E4E7EC] max-w-md">
              <DialogHeader>
                <DialogTitle className="text-[#1d3557] font-semibold">Invite new admin</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Full name</Label>
                  <Input
                    data-testid="admin-invite-name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="mt-1 rounded-md"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Work email</Label>
                  <Input
                    data-testid="admin-invite-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className="mt-1 rounded-md"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Admin role</Label>
                  <select
                    data-testid="admin-invite-role"
                    value={form.admin_role}
                    onChange={(e) => setForm((f) => ({ ...f, admin_role: e.target.value }))}
                    className="mt-1 w-full h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
                  >
                    {ADMIN_ROLES.map((r) => (
                      <option key={r} value={r}>{ADMIN_ROLE_LABEL[r] || r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Initial password</Label>
                  <Input
                    data-testid="admin-invite-password"
                    type="text"
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    className="mt-1 rounded-md font-mono"
                    placeholder="Min 8 characters"
                  />
                  <div className="text-[10px] text-[#667085] mt-1">Share this with the invited admin securely; they will be prompted to set 2FA on first login.</div>
                </div>
                <label className="flex items-center gap-2 text-[13px] text-[#1d3557] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.requires_2fa}
                    onChange={(e) => setForm((f) => ({ ...f, requires_2fa: e.target.checked }))}
                    data-testid="admin-invite-2fa"
                    className="accent-[#e63946]"
                  />
                  Require 2-factor at every sign-in
                </label>
                <Button
                  onClick={submitInvite}
                  disabled={saving}
                  data-testid="admin-invite-submit"
                  className="w-full h-10 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold"
                >
                  {saving ? "Inviting…" : "Send invite"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!isOwner && (
        <div className="rounded-md bg-[#F79009]/10 border border-[#F79009]/25 text-[#8a5a00] px-3 py-2 text-[12px]">
          You are viewing this page as <strong>{ADMIN_ROLE_LABEL[me?.admin_role]}</strong>. Only Platform Owner can invite or change admin members.
        </div>
      )}

      {loading ? (
        <div className="p-6 text-[13px] text-[#667085]">Loading admins…</div>
      ) : (
        <>
          {[
            { key: "platform_owner", title: "Platform Owner" },
            { key: "operations", title: "Operations & Governance" },
            { key: "readonly", title: "Read-only Executive" },
          ].map(({ key, title }) => (
            grouped[key].length > 0 && (
              <div key={key} className="bg-white rounded-xl border border-[#E4E7EC] overflow-hidden" data-testid={`admin-members-group-${key}`}>
                <div className="px-5 py-3.5 border-b border-[#F1FAEE]">
                  <h3 className="text-[13px] font-semibold text-[#1d3557] uppercase tracking-wider">{title}</h3>
                </div>
                <table className="w-full text-[13px]">
                  <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Admin</th>
                      <th className="text-left px-4 py-3 font-semibold">Role</th>
                      <th className="text-left px-4 py-3 font-semibold">2FA</th>
                      <th className="text-left px-4 py-3 font-semibold">Status</th>
                      <th className="text-left px-4 py-3 font-semibold">Created</th>
                      {isOwner && <th className="text-right px-4 py-3 font-semibold">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1FAEE]">
                    {grouped[key].map((m) => {
                      const isSelf = m.id === me?.id;
                      return (
                        <tr key={m.id} data-testid={`admin-member-row-${m.id}`}>
                          <td className="px-4 py-3">
                            <div className="text-[#1d3557] font-semibold">{m.name}</div>
                            <div className="text-[11px] text-[#667085] font-mono">{m.email}</div>
                          </td>
                          <td className="px-4 py-3 text-[11px] uppercase tracking-wider text-[#457b9d] font-semibold">
                            {ADMIN_ROLE_LABEL[m.admin_role] || m.admin_role}
                          </td>
                          <td className="px-4 py-3"><TwoFAPill on={!!m.requires_2fa} /></td>
                          <td className="px-4 py-3"><StatusPill active={!!m.active} /></td>
                          <td className="px-4 py-3 text-[11px] font-mono text-[#667085]">{fmtDateTime(m.created_at)}</td>
                          {isOwner && (
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-3">
                                <button
                                  onClick={() => patch(m.id, { requires_2fa: !m.requires_2fa }, m.requires_2fa ? "2FA turned off" : "2FA enforced")}
                                  data-testid={`admin-member-toggle-2fa-${m.id}`}
                                  className="text-[12px] font-semibold text-[#457b9d] hover:underline inline-flex items-center gap-1"
                                  title="Toggle 2FA enforcement"
                                >
                                  <KeyRound size={11} /> {m.requires_2fa ? "Disable 2FA" : "Require 2FA"}
                                </button>
                                {!isSelf && (
                                  m.active ? (
                                    <button
                                      onClick={() => window.confirm(`Lock ${m.email}?`) && patch(m.id, { active: false }, "Admin locked")}
                                      data-testid={`admin-member-lock-${m.id}`}
                                      className="text-[12px] font-semibold text-[#e63946] hover:underline inline-flex items-center gap-1"
                                    >
                                      <Ban size={11} /> Lock
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => patch(m.id, { active: true }, "Admin reactivated")}
                                      data-testid={`admin-member-unlock-${m.id}`}
                                      className="text-[12px] font-semibold text-[#12B76A] hover:underline inline-flex items-center gap-1"
                                    >
                                      <RotateCcw size={11} /> Reactivate
                                    </button>
                                  )
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ))}
        </>
      )}
    </div>
  );
}
